import React, { useState, useEffect } from "react";
import axios from "axios";
import { Calendar, ChevronLeft, ChevronRight, MapPin, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { API } from "../config";
import { formatHUF } from "../lib/format";
import Footer from "./components/Footer";
import { useFanClub } from "../contexts/AppContext";

export default function TourPage() {
  const [tourDates, setTourDates] = useState([]);
  const [viewMode, setViewMode] = useState('list');
  const [currentMonth, setCurrentMonth] = useState(new Date(2025, 1, 1));
  const { member } = useFanClub();

  useEffect(() => {
    const headers = member?.api_token ? { headers: { Authorization: `Bearer ${member.api_token}` } } : {};
    axios.get(`${API}/tour-dates`, headers).then(res => setTourDates(res.data)).catch(console.error);
  }, [member?.api_token]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return { firstDay: firstDay === 0 ? 6 : firstDay - 1, daysInMonth };
  };

  const getEventsForDay = (day) => {
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return tourDates.filter(t => t.date === dateStr);
  };

  const { firstDay, daysInMonth } = getDaysInMonth(currentMonth);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const emptyDays = Array.from({ length: firstDay }, (_, i) => i);

  return (
    <>
    <div className="min-h-screen pt-24 sm:pt-32 pb-32 px-4 sm:px-6 md:px-12 lg:px-24" data-testid="tour-page">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8 sm:mb-16 animate-fade-in flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-accent mb-2 sm:mb-4">Élő koncertek</p>
            <h1 className="text-3xl sm:text-5xl md:text-7xl font-bold font-['Syne'] tracking-tighter">Koncertek</h1>
          </div>
          <div className="flex gap-2">
            <Button variant={viewMode === 'list' ? 'default' : 'outline'} onClick={() => setViewMode('list')} className="rounded-full text-xs sm:text-sm">Lista</Button>
            <Button variant={viewMode === 'calendar' ? 'default' : 'outline'} onClick={() => setViewMode('calendar')} className="rounded-full text-xs sm:text-sm">
              <Calendar className="h-4 w-4 mr-1" />Naptár
            </Button>
          </div>
        </div>

        {viewMode === 'calendar' ? (
          <div className="glass rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 animate-fade-in">
            <div className="flex items-center justify-between mb-6">
              <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}><ChevronLeft className="h-5 w-5" /></Button>
              <h2 className="text-xl sm:text-2xl font-bold font-['Syne']">{currentMonth.toLocaleDateString('hu-HU', { year: 'numeric', month: 'long' })}</h2>
              <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}><ChevronRight className="h-5 w-5" /></Button>
            </div>
            <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2">
              {['H', 'K', 'Sze', 'Cs', 'P', 'Szo', 'V'].map(d => <div key={d} className="text-center text-xs sm:text-sm text-muted-foreground font-medium py-2">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1 sm:gap-2">
              {emptyDays.map(i => <div key={`empty-${i}`} className="aspect-square" />)}
              {days.map(day => {
                const events = getEventsForDay(day);
                return (
                  <div key={day} className={`aspect-square rounded-lg p-1 sm:p-2 relative ${events.length > 0 ? 'bg-primary/20 border border-primary/40' : 'bg-white/5'}`}>
                    <span className={`text-xs sm:text-sm ${events.length > 0 ? 'font-bold text-primary' : ''}`}>{day}</span>
                    {events.map(e => (
                      <div key={e.id} className="absolute bottom-1 left-1 right-1">
                        <div className={`text-[8px] sm:text-[10px] truncate px-1 rounded ${e.sold_out ? 'bg-red-500/30' : 'bg-primary/60'}`}>{e.city}</div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {tourDates.map((date, index) => (
              <div key={date.id} className="glass rounded-lg sm:rounded-xl p-4 sm:p-6 md:p-8 flex flex-row items-center gap-3 sm:gap-6 animate-fade-in card-hover" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="flex items-center gap-3 sm:gap-6 w-16 sm:w-auto md:w-48 shrink-0">
                  <div className="text-center">
                    <p className="text-xl sm:text-3xl font-bold font-['Syne'] text-primary">{new Date(date.date).getDate()}</p>
                    <p className="text-[10px] sm:text-sm uppercase tracking-wider text-muted-foreground">{new Date(date.date).toLocaleDateString('hu-HU', { month: 'short' })}</p>
                  </div>
                  <Separator orientation="vertical" className="h-12 bg-white/10 hidden md:block" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <h3 className="text-sm sm:text-xl font-bold font-['Syne'] truncate">{date.venue}</h3>
                    {date.event_type === 'festival' && <Badge className="bg-accent/20 text-accent text-[9px]">Fesztivál</Badge>}
                    {date.is_private && <Badge className="bg-primary/20 text-primary text-[9px]">Zártkörű</Badge>}
                    {date.is_free && <Badge className="bg-green-500/20 text-green-400 text-[9px]">Ingyenes</Badge>}
                  </div>
                  <p className="text-muted-foreground flex items-center gap-1 text-xs sm:text-base"><MapPin className="h-3 w-3 sm:h-4 sm:w-4 shrink-0" /><span className="truncate">{date.city}, {date.country}</span></p>
                  {date.ticket_price && <p className="text-primary font-semibold text-xs sm:text-sm mt-1">{formatHUF(date.ticket_price)}-tól</p>}
                </div>
                <div className="shrink-0 flex items-center gap-2 flex-wrap justify-end">
                  {date.sold_out && <Badge className="bg-accent/20 text-accent px-2 sm:px-4 py-1 text-[10px] sm:text-sm">Elfogyott</Badge>}
                  {date.is_private && <Badge className="bg-primary/20 text-primary px-2 sm:px-4 py-1 text-[10px] sm:text-sm">Zártkörű</Badge>}
                  {date.is_free && <Badge className="bg-green-500/20 text-green-400 px-2 sm:px-4 py-1 text-[10px] sm:text-sm">Ingyenes</Badge>}
                  {!date.sold_out && !date.is_private && !date.is_free && <Button className="rounded-full bg-primary hover:bg-primary/90 text-[10px] sm:text-sm px-2 sm:px-4 h-7 sm:h-10"><span className="hidden sm:inline">Jegyek</span><span className="sm:hidden">Jegy</span><ExternalLink className="h-3 w-3 ml-1" /></Button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
    <Footer />
    </>
  );
}
